'use client';

import React, {useMemo, createContext, useContext, ReactNode} from 'react';
import {Course} from '@udemy-v2/browse-course';
import {setRefIdToLocalStorage, getRefIdFromLocalStorage} from '@udemy-v2/utils';
import {generateTrackingId} from '@udemy-v2/event-tracking';
import {useEnrolledInCourse} from '@/features/course-landing-page/hooks/use-enrolled-in-course';
import {CourseWithVariationAggregation} from '@/types/course';

export type TrackedCourse = CourseWithVariationAggregation & {
    isDirectLanded?: boolean;
    frontendTrackingId?: string;
};

const CourseContext = createContext<TrackedCourse | null>(null);

interface CourseProviderProps {
    children: ReactNode;
    course: Course;
}

/**
 * Provider component that fetches and provides course data to its children
 */
export const ClpCourseProvider: React.FC<CourseProviderProps> = ({children, course}) => {
    const isEnrolledInCourse = useEnrolledInCourse(course.id);

    const isDirectLanded = useMemo(() => {
        let isDirectLandedLocal = true;
        if (typeof window !== 'undefined' && document.referrer) {
            try {
                if (new URL(document.referrer).host === location.host) {
                    isDirectLandedLocal = false;
                }
            } catch {
                // Referrer is an invalid URL, safe to ignore
            }
        }
        return isDirectLandedLocal;
    }, []);

    const courseTrackingId = useMemo(() => {
        // Only access localStorage on the client side
        if (typeof window === 'undefined') {
            // On server, generate a new tracking ID without localStorage
            return generateTrackingId();
        }

        const existingRefId = getRefIdFromLocalStorage(Number(course.id));
        if (existingRefId && !isDirectLanded) {
            return existingRefId;
        } else {
            const newRefId = generateTrackingId();
            setRefIdToLocalStorage(Number(course.id), newRefId);
            return newRefId;
        }
    }, [course.id, isDirectLanded]);

    return (
        <CourseContext.Provider
            value={{
                ...course,
                isEnrolled: isEnrolledInCourse,
                frontendTrackingId: courseTrackingId,
                isDirectLanded: isDirectLanded,
            }}
        >
            {children}
        </CourseContext.Provider>
    );
};

/**
 * Hook to access the course context
 * @returns The course data
 * @throws Error if used outside of a CourseProvider
 */
export const useClpCourse = (): TrackedCourse => {
    const context = useContext(CourseContext);

    if (context === null) {
        throw new Error('useCourse must be used within a ClpCourseProvider');
    }

    return context;
};
